import { Button } from '@kizen/kds/Button';
import { Typography } from '@kizen/kds/Typography';
import MultiSelect from '__components/Inputs/MultiSelect';
import Select from '__components/Inputs/Select';
import Input from '__components/Kizen/Input';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useContext, useState } from 'react';
import {
  AbsoluteLoader,
  ModalWrapper,
} from '__pages/Marketplace/components/SetupAssistant/styles';
import { SetupAssistantRow } from '__pages/Marketplace/components/SetupAssistant/inputs/Row';
import { Spacer } from '@kizen/kds/Spacer';
import { useTranslation } from 'react-i18next';
import {
  AssistantField,
  ModalConfig,
  RoutablePageConfig,
  SetupAssistantConfig,
} from '@kizenapps/engine';
import { type ButtonColor } from '@kizen/kds/Colors';
import {
  SetupAssistantController,
  useSetupAssistant,
} from '@kizenapps/engine/react';
import { PluginModalView } from './PluginModalView';
import CustomObjectsService from '__services/CustomObjectsService';
import { PluginContext } from './context';

interface PluginModalProps {
  modalState: {
    props: any;
    config: ModalConfig;
  };
  pluginApiName: string;
  pages?: RoutablePageConfig[];
}

const getInitialValues = (config: ModalConfig) => {
  const initialValues: Record<string, any> = {};

  config.content?.forEach((item) => {
    if (item.type === 'text' && item.defaultValue) {
      initialValues[item.id] = item.defaultValue;
    } else if (item?.type === 'dropdown' && item.defaultValue) {
      if (item.multiselect) {
        const options = item.options?.filter((opt) =>
          item.defaultValue?.includes(opt.value)
        );
        if (options?.length > 0) {
          initialValues[item.id] = options;
        }
      } else {
        const option = item.options?.find(
          (opt) => opt.value === item.defaultValue
        );
        if (option) {
          initialValues[item.id] = option;
        }
      }
    }
  });

  return initialValues;
};

const PluginModalPendingWrapper = (props: { children: any }) => {
  const {
    inferencePending,
    expressionsIdle,
    initialExpressionsPending,
    waitingExpressionCount,
  } = useSetupAssistant();

  const showBlockingLoader =
    inferencePending || !expressionsIdle || initialExpressionsPending;

  const showExpressionLoader = waitingExpressionCount > 0;

  return (
    <div>
      {props.children}
      {showBlockingLoader ? (
        <div className="absolute left-0 right-0 top-0 bottom-0">
          <AbsoluteLoader inline loading />
        </div>
      ) : null}
      {showExpressionLoader && !showBlockingLoader ? (
        <div className="absolute left-0 right-0 top-0 bottom-0">
          <AbsoluteLoader inline loading transparent />
        </div>
      ) : null}
    </div>
  );
};

const ContextParent = (props: {
  children: any;
  assistantConfig: SetupAssistantConfig;
  dynamicValues: Record<string, any>;
  setDynamicValues: (values: Record<string, any>) => void;
  plan: any;
  entitlements: any;
}) => {
  const {
    children,
    assistantConfig,
    dynamicValues,
    setDynamicValues,
    plan,
    entitlements,
  } = props;
  return (
    <SetupAssistantController
      config={assistantConfig}
      value={dynamicValues}
      onStateChange={setDynamicValues}
      getObjectByAPIName={CustomObjectsService.getObjectByAPIName}
      getCustomObjectDetails={CustomObjectsService.getCustomObjectDetails}
      entitlements={entitlements}
      plan={plan}
    >
      {children}
    </SetupAssistantController>
  );
};

const DynamicChild = ({
  modalState,
  dynamicValues,
  assistantConfig,
  pluginApiName,
}: {
  modalState: any;
  dynamicValues: Record<string, any>;
  assistantConfig: SetupAssistantConfig;
  pluginApiName: string;
}) => {
  const { props, config = {} } = modalState;
  const { t } = useTranslation();

  const { validateForm } = useSetupAssistant();

  return (
    <BasicModal
      size={config.size || 'small'}
      rightBtn={
        <Button
          variant={config.confirmButton?.variant}
          color={config.confirmButton?.color ?? 'primary'}
        >
          {config.confirmButton?.label ?? t('Confirm')}
        </Button>
      }
      actionButton={false}
      leftBtn={
        <Button
          variant={config.cancelButton?.variant ?? 'text'}
          color={config.cancelButton?.color ?? 'secondary'}
        />
      }
      defaultLeftBtnHandler={(...args: any) =>
        props?.onHide?.('button', ...args)
      }
      defaultLeftBtnText={config.cancelButton?.label ?? t('Cancel')}
      {...props}
      heading={config.title}
      onConfirm={async () => {
        const { isValid } = await validateForm();

        if (isValid) {
          props?.onConfirm?.(dynamicValues);
        }
      }}
      onHide={(...args: any) => props?.onHide?.('close', ...args)}
    >
      <PluginModalPendingWrapper>
        <ModalWrapper>
          {(assistantConfig.fields || []).map((field) => {
            return (
              <SetupAssistantRow
                key={field.key}
                field={field as AssistantField}
                pluginApiName={pluginApiName}
              />
            );
          })}
          <Spacer size={5} />
        </ModalWrapper>
      </PluginModalPendingWrapper>
    </BasicModal>
  );
};

export const PluginModal = ({
  modalState,
  pluginApiName,
  pages,
}: PluginModalProps) => {
  const [values, setValues] = useState<Record<string, any>>(() =>
    getInitialValues(modalState.config)
  );

  const { t } = useTranslation();

  const [dynamicValues, setDynamicValues] = useState<Record<string, any>>({});

  const { sessionInvalid, plan, entitlements } = useContext(PluginContext);

  if (!modalState || sessionInvalid) {
    return null;
  }

  const { props, config = {} } = modalState;

  // Custom views (routable pages) inside a modal
  if (config.viewId) {
    return (
      <PluginModalView
        config={config}
        props={props}
        pages={pages}
        pluginApiName={pluginApiName}
      />
    );
  }

  const content = (config.content as any) ?? [];

  const assistantConfig: SetupAssistantConfig = {
    fields: content,
  };

  if (config.dynamic) {
    return (
      <ContextParent
        assistantConfig={assistantConfig}
        dynamicValues={dynamicValues}
        setDynamicValues={setDynamicValues}
        plan={plan}
        entitlements={entitlements}
      >
        <DynamicChild
          modalState={modalState}
          dynamicValues={dynamicValues}
          assistantConfig={assistantConfig}
          pluginApiName={pluginApiName}
        />
      </ContextParent>
    );
  }

  return (
    <BasicModal
      rightBtn={
        <Button
          variant={config.confirmButton?.variant}
          color={(config.confirmButton?.color ?? 'primary') as ButtonColor}
        >
          {config.confirmButton?.label ?? t('Confirm')}
        </Button>
      }
      actionButton={false}
      leftBtn={
        <Button
          variant={config.cancelButton?.variant ?? 'text'}
          color={(config.cancelButton?.color ?? 'secondary') as ButtonColor}
        />
      }
      defaultLeftBtnHandler={(...args: any) =>
        props?.onHide?.('button', ...args)
      }
      defaultLeftBtnText={config.cancelButton?.label ?? t('Cancel')}
      {...props}
      heading={config.title}
      onConfirm={() => {
        props?.onConfirm?.(values);
      }}
      onHide={(...args: any) => props?.onHide?.('close', ...args)}
    >
      <div className="grid gap-[10px] grid-cols-2">
        {(config.content ?? []).map((item: any, index: number) => {
          let key = item.id;

          if (item.type === 'spacer') {
            key = `spacer-${index}`;
          } else if (item.type === 'description') {
            key = `description-${index}`;
          }

          if (!key) {
            return null;
          }

          return (
            <div
              key={item.id}
              className={item.widthPercent === 50 ? 'col-span-1' : 'col-span-2'}
            >
              {(
                {
                  text: (
                    <div>
                      <Input
                        label={item.label}
                        placeholder={item.placeholder}
                        variant="outline"
                        onChange={(val: string) => {
                          setValues({ ...values, [item.id]: val });
                        }}
                        value={values[item.id] ?? ''}
                      />
                    </div>
                  ),
                  description: (
                    <div>
                      <Typography>{item.content}</Typography>
                    </div>
                  ),
                  spacer: (
                    <div
                      className="h-[var(--spacer-height)]"
                      style={
                        {
                          '--spacer-height': `${item.height}px`,
                        } as any
                      }
                    />
                  ),
                  dropdown: item.multiselect ? (
                    <MultiSelect
                      options={item.options}
                      onChange={(v: any) => {
                        setValues({ ...values, [item.id]: v });
                      }}
                      value={values[item.id]}
                      label={item.label}
                      placeholder={item.placeholder}
                      menuInline
                    />
                  ) : (
                    <Select
                      options={item.options}
                      onChange={(v: any) =>
                        setValues({ ...values, [item.id]: v })
                      }
                      value={values[item.id]}
                      label={item.label}
                      placeholder={item.placeholder}
                      menuInline
                    />
                  ),
                } as any
              )[item.type] ?? null}
            </div>
          );
        })}
      </div>
    </BasicModal>
  );
};
